(function (global) {
  'use strict';

  const ALLOWED_CONFIDENCE_VALUES = ['authoritative', 'partial', 'fallback'];
  const ALLOWED_SOURCES = ['feeding', 'pricing', 'recommendation'];
  const ALLOWED_OPERATORS = ['exists', 'equals', 'in', 'isTrue'];

  /**
   * Creates a valid pending-approval Explanation Engine configuration scaffold.
   *
   * @returns {Object} Default Explanation Engine configuration.
   */
  function createDefaultExplanationEngineConfig() {
    return {
      schemaVersion: '1.0.0',
      status: 'pending-approval',
      explanationVersion: '1.0.0',
      recommendationKeyPassthrough: {
        enabled: true,
        type: 'recommendation.reason',
        codePrefix: 'recommendation.policy',
        confidence: 'authoritative',
        fallbackConfidence: 'fallback',
        priority: 100,
        parameterBindings: {
          recommendedProductId: 'objectiveCalculations.recommendedProductId',
          recommendationPolicyId: 'recommendationMetadata.recommendationPolicyId'
        },
        evidencePaths: [
          'presentationPayload.explanationKeys',
          'recommendationMetadata.recommendationPolicyId'
        ]
      },
      definitions: [],
      fallbackKeys: {
        invalidConfiguration: 'explanation.fallback.invalid_configuration',
        sourceUnavailable: 'explanation.fallback.source_unavailable',
        contactTeam: 'explanation.fallback.contact_team'
      },
      metadata: {
        owner: 'Content Team',
        status: 'Pending Approval',
        lastUpdated: null
      }
    };
  }

  function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  function getExplanationEngine(config) {
    if (!config || !isObject(config)) {
      return null;
    }

    if (config.explanationEngine) {
      return config.explanationEngine;
    }

    if (config.schemaVersion && (config.definitions || config.fallbackKeys)) {
      return config;
    }

    return null;
  }

  function readPath(source, path) {
    if (!isNonEmptyString(path)) {
      return { found: false, value: undefined };
    }

    const segments = path.split('.');
    let value = source;

    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];

      if (!value || (typeof value !== 'object' && typeof value !== 'function') ||
          !Object.prototype.hasOwnProperty.call(value, segment)) {
        return { found: false, value: undefined };
      }

      value = value[segment];
    }

    return { found: true, value: value };
  }

  function cloneValue(value) {
    if (Array.isArray(value)) {
      return value.map(cloneValue);
    }

    if (isObject(value)) {
      const copy = {};
      Object.keys(value).forEach(function (key) {
        copy[key] = cloneValue(value[key]);
      });
      return copy;
    }

    return value;
  }

  function copyParameters(source, bindings) {
    const parameters = {};

    if (!isObject(bindings)) {
      return parameters;
    }

    Object.keys(bindings).forEach(function (parameterName) {
      const result = readPath(source, bindings[parameterName]);
      if (result.found) {
        parameters[parameterName] = cloneValue(result.value);
      }
    });

    return parameters;
  }

  function createEvidence(sourceName, paths) {
    return (Array.isArray(paths) ? paths : []).map(function (path) {
      return {
        source: sourceName,
        path: path
      };
    });
  }

  function conditionMatches(source, condition) {
    const result = readPath(source, condition.path);

    switch (condition.operator) {
      case 'exists':
        return result.found && result.value !== null && result.value !== undefined;
      case 'equals':
        return result.found && result.value === condition.value;
      case 'in':
        return result.found && Array.isArray(condition.value) && condition.value.includes(result.value);
      case 'isTrue':
        return result.found && result.value === true;
      default:
        return false;
    }
  }

  function definitionMatches(source, definition) {
    return (definition.conditions || []).every(function (condition) {
      return conditionMatches(source, condition);
    });
  }

  function validateStringArray(values, path, errors) {
    if (!Array.isArray(values)) {
      errors.push(path + ' must be an array.');
      return;
    }

    values.forEach(function (value) {
      if (!isNonEmptyString(value)) {
        errors.push(path + ' entries must be non-empty strings.');
      }
    });
  }

  function validateBindings(bindings, path, errors) {
    if (bindings === undefined) {
      return;
    }

    if (!isObject(bindings)) {
      errors.push(path + ' must be an object.');
      return;
    }

    Object.keys(bindings).forEach(function (key) {
      if (!isNonEmptyString(key) || !isNonEmptyString(bindings[key])) {
        errors.push(path + ' must map non-empty parameter names to non-empty source paths.');
      }
    });
  }

  function validateConditions(conditions, path, errors) {
    if (!Array.isArray(conditions)) {
      errors.push(path + ' must be an array.');
      return;
    }

    conditions.forEach(function (condition, index) {
      const conditionPath = path + '[' + index + ']';

      if (!isObject(condition)) {
        errors.push(conditionPath + ' must be an object.');
        return;
      }

      if (!isNonEmptyString(condition.path)) {
        errors.push(conditionPath + '.path is required.');
      }

      if (!ALLOWED_OPERATORS.includes(condition.operator)) {
        errors.push(conditionPath + '.operator must be one of: ' + ALLOWED_OPERATORS.join(', ') + '.');
      }

      if (condition.operator === 'equals' && !Object.prototype.hasOwnProperty.call(condition, 'value')) {
        errors.push(conditionPath + '.value is required for equals.');
      }

      if (condition.operator === 'in' && !Array.isArray(condition.value)) {
        errors.push(conditionPath + '.value must be an array for in.');
      }
    });
  }

  function validateRecommendationPassthrough(passthrough, errors) {
    if (!isObject(passthrough)) {
      errors.push('explanationEngine.recommendationKeyPassthrough must be an object.');
      return;
    }

    if (typeof passthrough.enabled !== 'boolean') {
      errors.push('explanationEngine.recommendationKeyPassthrough.enabled must be a boolean.');
    }

    ['type', 'codePrefix'].forEach(function (key) {
      if (!isNonEmptyString(passthrough[key])) {
        errors.push('explanationEngine.recommendationKeyPassthrough.' + key + ' is required.');
      }
    });

    ['confidence', 'fallbackConfidence'].forEach(function (key) {
      if (!ALLOWED_CONFIDENCE_VALUES.includes(passthrough[key])) {
        errors.push('explanationEngine.recommendationKeyPassthrough.' + key + ' must be an allowed confidence value.');
      }
    });

    if (!isFiniteNumber(passthrough.priority)) {
      errors.push('explanationEngine.recommendationKeyPassthrough.priority must be a finite number.');
    }

    validateBindings(passthrough.parameterBindings, 'explanationEngine.recommendationKeyPassthrough.parameterBindings', errors);
    validateStringArray(passthrough.evidencePaths, 'explanationEngine.recommendationKeyPassthrough.evidencePaths', errors);
  }

  /**
   * Validates the Explanation Engine section of a full or standalone configuration.
   *
   * @param {Object} config Full Nutrition Advisor or standalone Explanation Engine configuration.
   * @returns {{isValid: boolean, errors: string[]}} Validation result.
   */
  function validateExplanationEngineConfig(config) {
    const errors = [];
    const explanationEngine = getExplanationEngine(config);

    if (!explanationEngine) {
      return { isValid: false, errors: ['explanationEngine configuration is missing.'] };
    }

    if (explanationEngine.schemaVersion !== '1.0.0') {
      errors.push('explanationEngine.schemaVersion must be 1.0.0.');
    }

    if (!isNonEmptyString(explanationEngine.status)) {
      errors.push('explanationEngine.status is required.');
    }

    if (!isNonEmptyString(explanationEngine.explanationVersion)) {
      errors.push('explanationEngine.explanationVersion is required.');
    }

    validateRecommendationPassthrough(explanationEngine.recommendationKeyPassthrough, errors);

    if (!Array.isArray(explanationEngine.definitions)) {
      errors.push('explanationEngine.definitions must be an array.');
    } else {
      const ids = new Set();
      const codes = new Set();

      explanationEngine.definitions.forEach(function (definition, index) {
        const path = 'explanationEngine.definitions[' + index + ']';

        if (!isObject(definition)) {
          errors.push(path + ' must be an object.');
          return;
        }

        ['id', 'code', 'type', 'key'].forEach(function (key) {
          if (!isNonEmptyString(definition[key])) {
            errors.push(path + '.' + key + ' is required.');
          }
        });

        if (ids.has(definition.id)) {
          errors.push('explanationEngine.definitions must not contain duplicate ids.');
        }
        ids.add(definition.id);

        if (codes.has(definition.code)) {
          errors.push('explanationEngine.definitions must not contain duplicate codes.');
        }
        codes.add(definition.code);

        if (!ALLOWED_SOURCES.includes(definition.source)) {
          errors.push(path + '.source must be one of: ' + ALLOWED_SOURCES.join(', ') + '.');
        }

        if (!ALLOWED_CONFIDENCE_VALUES.includes(definition.confidence)) {
          errors.push(path + '.confidence must be an allowed confidence value.');
        }

        if (!isFiniteNumber(definition.priority)) {
          errors.push(path + '.priority must be a finite number.');
        }

        validateConditions(definition.conditions, path + '.conditions', errors);
        validateBindings(definition.parameterBindings, path + '.parameterBindings', errors);
        validateStringArray(definition.evidencePaths, path + '.evidencePaths', errors);
      });
    }

    if (!isObject(explanationEngine.fallbackKeys)) {
      errors.push('explanationEngine.fallbackKeys must be an object.');
    } else {
      ['invalidConfiguration', 'sourceUnavailable', 'contactTeam'].forEach(function (key) {
        if (!isNonEmptyString(explanationEngine.fallbackKeys[key])) {
          errors.push('explanationEngine.fallbackKeys.' + key + ' is required.');
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Creates a pending explanation bundle without fabricated explanation items.
   *
   * @returns {Object} Placeholder explanation bundle.
   */
  function createPlaceholderExplanation() {
    return {
      status: 'pending-approval',
      explanationVersion: '1.0.0',
      contactTeamFallback: false,
      fallbackReason: null,
      unavailableSources: ALLOWED_SOURCES.slice(),
      items: []
    };
  }

  function createFallbackItem(reason, key) {
    return {
      id: 'explanation-fallback-' + reason,
      code: 'explanation.fallback.' + reason,
      type: 'fallback',
      key: key,
      parameters: {
        reason: reason
      },
      evidence: [],
      priority: 1000,
      confidence: 'fallback'
    };
  }

  function createInvalidConfigurationExplanation() {
    const defaultConfig = createDefaultExplanationEngineConfig();
    return {
      status: 'contact-team',
      explanationVersion: defaultConfig.explanationVersion,
      contactTeamFallback: true,
      fallbackReason: 'invalid-explanation-engine-configuration',
      unavailableSources: [],
      items: [createFallbackItem(
        'invalid-configuration',
        defaultConfig.fallbackKeys.invalidConfiguration
      )]
    };
  }

  function createRecommendationItems(recommendationPlan, passthrough) {
    if (!recommendationPlan || !passthrough.enabled) {
      return [];
    }

    const keyResult = readPath(recommendationPlan, 'presentationPayload.explanationKeys');
    const policyResult = readPath(recommendationPlan, 'recommendationMetadata.recommendationPolicyId');
    const keys = keyResult.found && Array.isArray(keyResult.value) ? keyResult.value : [];
    const policyId = policyResult.found && isNonEmptyString(policyResult.value)
      ? policyResult.value
      : 'unknown';
    const confidence = recommendationPlan.contactTeamFallback
      ? passthrough.fallbackConfidence
      : passthrough.confidence;

    return keys.filter(isNonEmptyString).map(function (key, index) {
      const ordinal = index + 1;
      return {
        id: 'recommendation-' + policyId + '-explanation-' + ordinal,
        code: passthrough.codePrefix + '.' + policyId + '.explanation.' + ordinal,
        type: passthrough.type,
        key: key,
        parameters: copyParameters(recommendationPlan, passthrough.parameterBindings),
        evidence: createEvidence('recommendation', passthrough.evidencePaths),
        priority: passthrough.priority,
        confidence: confidence
      };
    });
  }

  function createDefinitionItems(outputs, definitions) {
    return definitions.reduce(function (items, definition) {
      const source = outputs[definition.source];

      if (!source || !definitionMatches(source, definition)) {
        return items;
      }

      items.push({
        id: definition.id,
        code: definition.code,
        type: definition.type,
        key: definition.key,
        parameters: copyParameters(source, definition.parameterBindings),
        evidence: createEvidence(definition.source, definition.evidencePaths),
        priority: definition.priority,
        confidence: definition.confidence
      });

      return items;
    }, []);
  }

  function createSourceFallbackItems(outputs, fallbackKeys) {
    return ALLOWED_SOURCES.reduce(function (items, sourceName) {
      const source = outputs[sourceName];

      if (!source || !source.contactTeamFallback) {
        return items;
      }

      const reason = isNonEmptyString(source.fallbackReason) ? source.fallbackReason : 'source-unavailable';
      items.push({
        id: sourceName + '-fallback-' + reason,
        code: 'explanation.' + sourceName + '.fallback.' + reason,
        type: sourceName + '.fallback',
        key: fallbackKeys.contactTeam,
        parameters: {
          source: sourceName,
          reason: reason
        },
        evidence: createEvidence(sourceName, ['contactTeamFallback', 'fallbackReason']),
        priority: 900,
        confidence: 'fallback'
      });

      return items;
    }, []);
  }

  function uniqueByCode(items) {
    const seenCodes = new Set();
    return items.filter(function (item) {
      if (seenCodes.has(item.code)) {
        return false;
      }
      seenCodes.add(item.code);
      return true;
    });
  }

  /**
   * Builds a structured explanation bundle from completed upstream engine outputs.
   *
   * @param {Object} engineOutputs Feeding, Pricing, and Recommendation Engine outputs.
   * @param {Object} config Full Nutrition Advisor or standalone Explanation Engine configuration.
   * @returns {Object} Structured explanation bundle containing keys and copied parameters.
   */
  function buildExplanation(engineOutputs, config) {
    const validation = validateExplanationEngineConfig(config || {});
    const explanationEngine = getExplanationEngine(config || {});

    if (!validation.isValid || !explanationEngine) {
      return createInvalidConfigurationExplanation();
    }

    const safeOutputs = isObject(engineOutputs) ? engineOutputs : {};
    const outputs = {
      feeding: safeOutputs.feedingPlan || null,
      pricing: safeOutputs.pricingPlan || null,
      recommendation: safeOutputs.recommendationPlan || null
    };
    const unavailableSources = ALLOWED_SOURCES.filter(function (sourceName) {
      return !outputs[sourceName];
    });
    const contactTeamFallback = ALLOWED_SOURCES.some(function (sourceName) {
      return !!(outputs[sourceName] && outputs[sourceName].contactTeamFallback);
    });

    let items = createRecommendationItems(outputs.recommendation, explanationEngine.recommendationKeyPassthrough)
      .concat(createDefinitionItems(outputs, explanationEngine.definitions))
      .concat(createSourceFallbackItems(outputs, explanationEngine.fallbackKeys));

    if (!items.length) {
      items.push(createFallbackItem('source-unavailable', explanationEngine.fallbackKeys.sourceUnavailable));
    }

    items = uniqueByCode(items).sort(function (left, right) {
      return right.priority - left.priority;
    });

    return {
      status: contactTeamFallback ? 'contact-team' : (unavailableSources.length ? 'partial' : 'explained'),
      explanationVersion: explanationEngine.explanationVersion,
      contactTeamFallback: contactTeamFallback,
      fallbackReason: contactTeamFallback ? 'upstream-contact-team-fallback' : null,
      unavailableSources: unavailableSources,
      items: items
    };
  }

  const api = {
    createDefaultExplanationEngineConfig,
    validateExplanationEngineConfig,
    createPlaceholderExplanation,
    buildExplanation
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.ExplanationEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
